import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import clsx from 'clsx';

import {zodResolver} from '@hookform/resolvers/zod';
import {Button, BUTTON_VARIANTS, ButtonWithLoading, Input, INPUT_VARIANTS, Typography} from '@nur-portal/ui-kit';

import {Timer} from '../../components/timer';
import {UNHANDLED_ERROR_KEY} from '../../helpers/common-error';
import {maskPhoneNumber} from '../../helpers/phone-number';
import {useAutoFocus} from '../../hooks/useAutoFocus';

import {FIELD_NAME, SMSCodeSchema} from './validation';

import styles from './sms-code.module.css';

const SMSCodeForm = ({
  phoneNumber,
  responseError,
  SMSCodeTTL,
  isLoading,
  setSMSCodeTTL,
  formSubmitCallback,
  formErrorCallback = () => {},
  onCancelClick,
  onSubmitClick,
  onResendSMSCode
}) => {
  const {t} = useTranslation();

  const {
    handleSubmit,
    setError,
    watch,
    setValue,
    clearErrors,
    formState: {
      errors
    }
  } = useForm({
    resolver: zodResolver(SMSCodeSchema),
    defaultValues: {
      [FIELD_NAME]: ''
    }
  });

  useEffect(() => {
    clearErrors(FIELD_NAME);
  }, []);

  useEffect(() => {
    if (responseError) {
      setError(FIELD_NAME, responseError);
    }
  }, [responseError, setError]);

  const inputRef = useAutoFocus();

  const SMSCodeValue = watch(FIELD_NAME);

  const errorText = errors[FIELD_NAME]
    ? t(`errors:SMSCode.${errors[FIELD_NAME].message}`, t(UNHANDLED_ERROR_KEY))
    : '';

  return (
    <form onSubmit={handleSubmit(formSubmitCallback, formErrorCallback)}>
      <Typography variant="title-16" className={styles.title}>
        {t('forms:SMSCode.title')}
      </Typography>
      <Typography variant="text-14" className={styles.subtitle}>
        <Trans
          i18nKey='forms:SMSCode.subTitle'
          components={{
            text: <b />
          }}
          values={{phoneNumber: maskPhoneNumber(phoneNumber)}}
        />
      </Typography>
      <Input
        ref={inputRef}
        errorText={errorText}
        errorClassName={styles.inputError}
        className={styles.codeInput}
        value={SMSCodeValue}
        variant={INPUT_VARIANTS.SECONDARY}
        type="number"
        placeholder={t('forms:SMSCode.placeholder')}
        inputMode="tel"
        onChange={event => {
          setValue(FIELD_NAME, event.target.value);
          clearErrors(FIELD_NAME);
        }}
      />
      <div>
        <Button
          type="button"
          variant={BUTTON_VARIANTS.LINK}
          onClick={onResendSMSCode}
          className={clsx(
            styles.resendLink,
            {
              [styles.withTimer]: SMSCodeTTL > 0
            })}
        >
          {t('forms:SMSCode.resendSms')}
          {SMSCodeTTL > 0 && (
            <>
              {t('forms:SMSCode.resendSmsAfter')}
              <Timer
                seconds={SMSCodeTTL}
                onFinish={() => setSMSCodeTTL(0)}
              />
            </>
          )}
        </Button>
      </div>
      <div className={styles.actionButtons}>
        <Button
          variant={BUTTON_VARIANTS.OUTLINE}
          onClick={onCancelClick}
          className={styles.actionButton}
          type="button"
        >
          {t('forms:SMSCode.actionButtons.cancel')}
        </Button>
        <ButtonWithLoading
          isLoading={isLoading}
          type="submit"
          onClick={onSubmitClick}
          className={styles.actionButton}
        >
          {t('forms:SMSCode.actionButtons.submit')}
        </ButtonWithLoading>
      </div>
    </form>
  );
};

export {SMSCodeForm};
